import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { apiParams } from '../lib/filters.js';
import { fillFor } from '../lib/patterns.js';
import {
  bridgeContributorColumns,
  bridgeStepColumns,
  bridgeStepRows,
  waterfall,
} from '../lib/bridge.js';
import { withFormats } from '../lib/table.js';
import { STATUS_STATES } from '../lib/palette.js';
import {
  hours as formatHours,
  percent,
  weekLabel,
  count,
  humanize,
  shortDate,
} from '../lib/format.js';

/** The filters this view reads, in the API's names. Anything else in the bar leaves it alone. */
const PARAM_KEYS = ['run_id', 'from', 'weeks', 'dimension', 'entity', 'pipeline'];

/** Like the ribbon, the bridge has no project dimension: a project against company capacity misleads. */
const BRIDGE_DIMENSIONS = ['company', 'department', 'service', 'office', 'individual'];

/** Short axis labels; the full step names are in the legend, the tooltip and the table. */
const AXIS_LABELS = {
  gross: 'Gross',
  time_off: 'Time off',
  nonproject: 'Non-project',
  recurring: 'Recurring',
  committed_explicit: 'Explicit',
  committed_inferred: 'Inferred',
  pipeline: 'Pipeline',
  headroom: 'Headroom',
};

const GROUP_NOUNS = {
  person: ['person', 'people'],
  project: ['project', 'projects'],
  opportunity: ['deal', 'deals'],
  service: ['service', 'services'],
};
const noun = (group, n) => GROUP_NOUNS[group]?.[n === 1 ? 0 : 1] ?? group;

/**
 * The capacity bridge (V-10, Phase 21): a waterfall from gross capacity through time off, the
 * non-project load, the recurring baseline, the committed work (explicit, then inferred) and the
 * pipeline to the headroom — or, below zero, the overload — for the whole window or one week of
 * it. Every step is clickable: the contributors behind it (people, projects, deals, or services)
 * open beneath the chart with their hours, and total the bar.
 *
 * Everything drawn here comes from `GET /forecast/bridge` and `GET /forecast/bridge/contributors`;
 * the component adds no arithmetic beyond the waterfall geometry of the numbers it was given
 * (`lib/bridge.js`), and that geometry lands on the API's own headroom or says it does not.
 * The five demand steps are the ribbon's layers in the ribbon's colours, so a value checked on
 * the ribbon is found in the same colour one card down.
 *
 * Bars are keyed (`repeat` on the step key), so a filter or week change updates the surviving
 * bars in place rather than repainting them (PRD 7.3). As with the ribbon, the marks render
 * inside the chart frame's shadow root, so every mark carries its presentation as attributes.
 */
class CapacityBridge extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _week: { state: true },
    _hover: { state: true },
    _step: { state: true },
    _contrib: { state: true },
    _contribLoading: { state: true },
    _contribError: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._loading = false;
    this._error = null;
    this._week = null; // null = the whole window; otherwise a week_start
    this._hover = null;
    this._step = null; // the selected step key, or null
    this._contrib = null;
    this._contribLoading = false;
    this._contribError = null;
    this._tableOpen = false;
    this._requestKey = null;
    this._contribKey = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      h3 {
        margin: 0;
        font-size: 15px;
        line-height: 1.3;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
        align-items: flex-end;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 14px;
        margin-bottom: 6px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      select,
      button {
        font: inherit;
        font-size: 13px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: 6px;
        padding: 4px 8px;
      }
      button {
        cursor: pointer;
      }
      button.link {
        border: 0;
        background: none;
        color: var(--link);
        padding: 0;
        font-weight: 600;
      }
      .legend-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 18px;
        margin-top: 10px;
        font-size: 12.5px;
        color: var(--ink);
      }
      .key {
        display: inline-flex;
        align-items: center;
        gap: 7px;
      }
      .key svg {
        display: block;
        flex: none;
      }
      .note {
        margin: 10px 0 0;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .note.warn {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      .state {
        padding: 36px 0;
        text-align: center;
        color: var(--ink-muted);
      }
      .state.error {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      .contributors {
        margin-top: 16px;
        border-top: 1px solid var(--border-soft);
        padding-top: 14px;
      }
      .contributors .bar {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 14px;
        margin-bottom: 10px;
      }
      .contributors .bar .close {
        margin-left: auto;
      }
      details.table {
        margin-top: 16px;
        border-top: 1px solid var(--border-soft);
        padding-top: 12px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        color: var(--link);
        list-style: none;
      }
      details.table summary::-webkit-details-marker {
        display: none;
      }
      details.table summary::before {
        content: '▸ ';
        color: var(--ink-muted);
      }
      details.table[open] summary::before {
        content: '▾ ';
      }
      details.table data-table,
      .contributors data-table {
        margin-top: 10px;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    // Fetch off the store, not off the render cycle (see demand-ribbon.js).
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  // -------------------------------------------------------------------------------------------
  // Data

  /** The request this view wants for the current filters, or null when there is no run. */
  _params() {
    const { filters, run } = this.appState;
    if (!run) return null;
    const params = apiParams(filters, PARAM_KEYS);
    if (!BRIDGE_DIMENSIONS.includes(params.dimension)) {
      delete params.dimension;
      delete params.entity;
    }
    return params;
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      this._contrib = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/forecast/bridge', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
      // A week chosen before that is not in the new window falls back to the whole window.
      if (this._week && !data.weeks.some((w) => w.week_start === this._week)) this._week = null;
      this._maybeFetchContributors();
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  /** The contributors request for the selected step and week, on the bridge's own parameters. */
  _contribParams() {
    const params = this._params();
    if (!params || !this._step) return null;
    const out = { ...params, step: this._step };
    if (this._week) {
      out.from = this._week;
      out.weeks = 1;
    }
    return out;
  }

  async _maybeFetchContributors() {
    const params = this._contribParams();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._contribKey) return;
    this._contribKey = key;
    if (!params) {
      this._contrib = null;
      return;
    }
    this._contribLoading = true;
    this._contribError = null;
    try {
      const data = await api.get('/forecast/bridge/contributors', params);
      if (this._contribKey !== key) return;
      this._contrib = data;
    } catch (err) {
      if (this._contribKey !== key) return;
      this._contribError = err.message;
      this._contrib = null;
    } finally {
      if (this._contribKey === key) this._contribLoading = false;
    }
  }

  get _steps() {
    return this._data?.steps ?? [];
  }

  /** The week row shown, or null for the whole window. */
  get _weekRow() {
    if (!this._week) return null;
    return this._data?.weeks.find((w) => w.week_start === this._week) ?? null;
  }

  /** The bridge drawn: the chosen week's, or the window total. */
  get _bridge() {
    const d = this._data;
    if (!d) return null;
    return this._weekRow?.bridge ?? d.bridge;
  }

  get _utilization() {
    return this._weekRow ? this._weekRow.utilization : (this._data?.utilization ?? null);
  }

  get _band() {
    return this._weekRow ? this._weekRow.band : (this._data?.band ?? null);
  }

  get _waterfall() {
    return waterfall(this._bridge ?? {});
  }

  _selectStep(key) {
    this._step = this._step === key ? null : key;
    this._maybeFetchContributors();
  }

  _selectWeek(value) {
    this._week = value || null;
    this._maybeFetchContributors();
  }

  // -------------------------------------------------------------------------------------------
  // Drawing

  /** The paint of a step's bar: the anchor in strong ink, time off outlined, layers as the ribbon, headroom as its state. */
  _paint(step) {
    if (step.kind === 'start') return { fill: 'var(--ink-strong)', stroke: 'var(--surface)' };
    if (step.fill === 'outline')
      return { fill: 'var(--surface)', stroke: 'var(--ink-muted)', dash: '4 3' };
    if (step.kind === 'result') {
      const state = STATUS_STATES.find((s) => s.key === this._band);
      return {
        fill: state ? `var(${state.var})` : 'var(--ink-muted)',
        stroke: 'var(--surface)',
      };
    }
    return { fill: fillFor(step.token, step.fill), stroke: 'var(--surface)' };
  }

  _marks = ({ x, y, plot, svg: s }) => {
    const steps = this._steps;
    const wf = this._waterfall;
    const bars = new Map(wf.bars.map((b) => [b.key, b]));
    const bandwidth = x.bandwidth();
    const gap = x.step() - bandwidth;
    const zero = y(0);
    const signed = (step, bar) => {
      if (step.kind === 'deduction') return bar.hours > 0 ? -bar.hours : 0;
      return bar.hours;
    };
    return s`
      <g class="connectors">
        ${steps.slice(0, -1).map((step, i) => {
          const bar = bars.get(step.key);
          const next = steps[i + 1];
          const level = bar.after;
          return s`<line x1=${x(step.key) + bandwidth} x2=${x(next.key)}
                         y1=${y(level)} y2=${y(level)}
                         stroke="var(--ink-muted)" stroke-width="1" stroke-dasharray="2 2" />`;
        })}
      </g>
      ${
        wf.min < 0
          ? s`<line class="zero" x1=${plot.left} x2=${plot.left + plot.width} y1=${zero} y2=${zero}
                    stroke="var(--axis)" stroke-width="1.5" />`
          : nothing
      }
      <g class="bars">
        ${repeat(
          steps,
          (step) => step.key,
          (step) => {
            const bar = bars.get(step.key);
            const paint = this._paint(step);
            const top = y(bar.to);
            const height = Math.max(bar.hours === 0 ? 1 : 2, y(bar.from) - top);
            const selected = this._step === step.key;
            const hovered = this._hover === step.key;
            const below = step.kind === 'result' && bar.hours < 0;
            return s`
              <g class="step" data-step=${step.key} data-kind=${step.kind}>
                <rect class="bar" x=${x(step.key)} width=${bandwidth} y=${top} height=${height} rx="1.5"
                      fill=${paint.fill} stroke=${selected ? 'var(--focus)' : hovered ? 'var(--ink-strong)' : paint.stroke}
                      stroke-width=${selected ? 2.5 : hovered ? 1.5 : 1}
                      stroke-dasharray=${paint.dash ?? 'none'} />
                <text class="value" x=${x(step.key) + bandwidth / 2}
                      y=${below ? y(bar.from) + 13 : top - 5} text-anchor="middle"
                      fill="var(--ink-strong)" font-size="11" font-weight="600"
                      font-family="var(--font-sans)">${formatHours(signed(step, bar), { exact: true })}</text>
              </g>`;
          },
        )}
      </g>
      <g class="hits">
        ${steps.map((step) => {
          const bar = bars.get(step.key);
          return s`
            <rect class="hit" x=${x(step.key) - gap / 2} width=${x.step()}
                  y=${plot.top} height=${plot.height} fill="transparent" style="cursor: pointer"
                  @pointermove=${(e) => this._showTooltip(e, step.key)}
                  @pointerleave=${() => this._hideTooltip()}
                  @focus=${(e) => this._showTooltip(e, step.key)}
                  @blur=${() => this._hideTooltip()}
                  @click=${() => this._selectStep(step.key)}
                  @keydown=${(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      this._selectStep(step.key);
                    }
                  }}
                  tabindex="0" role="button" aria-pressed=${this._step === step.key ? 'true' : 'false'}
                  aria-label=${`${step.label}: ${formatHours(bar.hours)}. Show contributors`} />`;
        })}
      </g>
    `;
  };

  // -------------------------------------------------------------------------------------------
  // Hover

  _showTooltip(event, key) {
    const step = this._steps.find((st) => st.key === key);
    const bar = this._waterfall.bars.find((b) => b.key === key);
    if (!step || !bar) return;
    this._hover = key;
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const gross = this._bridge?.gross_hours ?? 0;
    const rows = [];
    if (step.kind === 'start') {
      rows.push({ label: 'Gross capacity', value: formatHours(bar.hours, { exact: true }) });
      rows.push({ label: 'People', value: count(this._bridge?.people) });
    } else if (step.kind === 'deduction') {
      rows.push({
        label: step.label,
        value: formatHours(-bar.hours, { exact: true }),
        token: step.layer ? step.token : undefined,
        fill: step.layer ? step.fill : undefined,
      });
      rows.push({ label: 'Remaining after', value: formatHours(bar.after, { exact: true }) });
      if (gross > 0) rows.push({ label: 'Share of gross', value: percent(bar.hours / gross) });
      if (step.key === 'time_off' && this._bridge) {
        const b = this._bridge;
        rows.push({ label: 'Holiday', value: formatHours(b.holiday_hours, { exact: true }) });
        rows.push({ label: 'PTO', value: formatHours(b.pto_hours, { exact: true }) });
        if (b.reduced_hours)
          rows.push({
            label: 'Reduced hours',
            value: formatHours(b.reduced_hours, { exact: true }),
          });
      }
    } else {
      const over = bar.hours < 0;
      rows.push({
        label: over ? 'Over capacity' : 'Headroom',
        value: formatHours(Math.abs(bar.hours), { exact: true }),
      });
      rows.push({
        label: 'Total demand',
        value: formatHours(this._bridge?.demand_hours, { exact: true }),
      });
      rows.push({
        label: 'Available capacity',
        value: formatHours(this._bridge?.capacity_hours, { exact: true }),
      });
    }
    const state = STATUS_STATES.find((st) => st.key === this._band);
    const u = this._utilization;
    const footer = [
      step.kind === 'result'
        ? u === null
          ? 'No capacity in this window'
          : `${percent(u)} allocated${state ? ` · ${state.icon} ${state.label}` : ''}`
        : null,
      `Click for the ${noun(step.contributors, 2)} behind it`,
    ]
      .filter(Boolean)
      .join(' · ');
    tooltip.show({
      x: event.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
      y: event.clientY ?? (rect ? rect.top : 0),
      title: `${step.label} · ${this._scopeLabel()}`,
      rows,
      footer,
    });
  }

  _hideTooltip() {
    this._hover = null;
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Tables

  get _stepColumns() {
    // The specification is shared with the server's CSV (lib/bridge.js); only the drawing is here.
    return withFormats(bridgeStepColumns(), {
      byUnit: {
        hours: (v) => formatHours(v, { exact: true }),
        percent: (v) => percent(v),
        date: (v) => (v ? shortDate(v) : '—'),
        text: (v) => v ?? '—',
      },
      byKey: {
        label: (v) => v,
        group: (v) => humanize(v),
      },
    });
  }

  get _stepRows() {
    // Shared with the server's CSV (lib/bridge.js), so the file is the table.
    return bridgeStepRows({ ...this._data, totals: this._bridge, steps: this._steps });
  }

  get _contribColumns() {
    return withFormats(
      bridgeContributorColumns({
        group: this._contrib?.step.contributors,
        step: this._contrib?.step.key,
      }),
      {
        byUnit: {
          hours: (v) => formatHours(v, { exact: true }),
          percent: (v) => percent(v),
          date: (v) => (v ? shortDate(v) : '—'),
          text: (v) => v ?? '—',
        },
        byKey: {
          label: (v) => v,
          canonical_service: (v) => humanize(v),
          canonical_type: (v) => humanize(v),
          confidence_tier: (v) => humanize(v),
          source_modes: (v) => (v ?? []).map(humanize).join(', '),
          slipped: (v) => (v ? 'yes' : ''),
          band: (v) => (v ? html`<status-badge state=${v} compact></status-badge>` : '—'),
        },
      },
    );
  }

  // -------------------------------------------------------------------------------------------
  // Render

  _scopeLabel() {
    const d = this._data;
    if (!d) return '';
    const who = d.entity ? d.entity.label : 'Whole company';
    const when = this._week
      ? `week of ${shortDate(this._week)}`
      : `${d.weeks_count} weeks from ${weekLabel(d.from)}`;
    return `${who} · ${when}`;
  }

  _subtitle() {
    const d = this._data;
    const { filters } = this.appState;
    if (!d) return '';
    const who = d.entity ? `${d.entity.label} (${d.dimension})` : 'Whole company';
    const notes = [];
    if (!BRIDGE_DIMENSIONS.includes(filters.dimension))
      notes.push('the bridge has no project dimension, so the company is shown');
    return `${who} · ${d.weeks_count} weeks from ${weekLabel(d.from)} · pipeline ${d.pipeline_mode}${
      notes.length ? ` · ${notes.join('; ')}` : ''
    }`;
  }

  _kpis() {
    const b = this._bridge;
    if (!b) return nothing;
    const over = b.headroom_hours < 0;
    const u = this._utilization;
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${formatHours(b.gross_hours)}</div>
          <div class="l">gross, ${count(b.people)} people</div>
        </div>
        <div class="kpi">
          <div class="n">${formatHours(b.demand_hours)}</div>
          <div class="l">demand${u === null ? '' : `, ${percent(u)} allocated`}</div>
        </div>
        <div class="kpi">
          <div class="n">${formatHours(Math.abs(b.headroom_hours))}</div>
          <div class="l">
            ${over ? 'over capacity' : 'headroom'}
            ${
              this._band ? html`<status-badge state=${this._band} compact></status-badge>` : nothing
            }
          </div>
        </div>
      </div>
    `;
  }

  _weekSelect() {
    const d = this._data;
    if (!d) return nothing;
    return html`
      <label class="controls">
        <span>Show</span>
        <select
          aria-label="Bridge for the window or one week"
          .value=${this._week ?? ''}
          @change=${(e) => this._selectWeek(e.target.value)}
        >
          <option value="" ?selected=${!this._week}>Whole window (${d.weeks_count} weeks)</option>
          ${d.weeks.map(
            (w) => html`
              <option value=${w.week_start} ?selected=${this._week === w.week_start}>
                Week of
                ${weekLabel(w.week_start)}${
                  d.peak?.week_start === w.week_start ? ' · peak' : ''
                }${w.band ? ` · ${w.band}` : ''}
              </option>
            `,
          )}
        </select>
        ${
          d.peak && this._week !== d.peak.week_start
            ? html`<button class="link" @click=${() => this._selectWeek(d.peak.week_start)}>
                Jump to the peak week (${weekLabel(d.peak.week_start)},
                ${percent(d.peak.utilization)})
              </button>`
            : nothing
        }
      </label>
    `;
  }

  _legend() {
    const layers = this._steps.filter((s) => s.layer);
    const headroomPaint = this._paint({ kind: 'result' });
    const swatch = (fill, stroke, dash) => html`
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
        <rect
          x="0.75"
          y="0.75"
          width="12.5"
          height="12.5"
          rx="3"
          fill=${fill}
          stroke=${stroke}
          stroke-width="1.5"
          stroke-dasharray=${dash ?? 'none'}
        />
      </svg>
    `;
    return html`
      <div class="legend-row">
        <span class="key"
          >${swatch('var(--ink-strong)', 'var(--ink-strong)')}<span>Gross capacity</span></span
        >
        <span class="key"
          >${swatch('var(--surface)', 'var(--ink-muted)', '3 2')}<span>Time off</span></span
        >
        <chart-legend
          .items=${layers.map((l) => ({ label: l.label, token: l.token, fill: l.fill }))}
        ></chart-legend>
        <span class="key"
          >${swatch(headroomPaint.fill, headroomPaint.fill)}<span
            >${(this._bridge?.headroom_hours ?? 0) < 0 ? 'Over capacity' : 'Headroom'}</span
          ></span
        >
      </div>
    `;
  }

  _contributors() {
    if (!this._step) return nothing;
    const step = this._steps.find((s) => s.key === this._step);
    const c = this._contrib;
    let body;
    if (this._contribError) body = html`<div class="state error">${this._contribError}</div>`;
    else if (!c || c.step.key !== this._step)
      body = html`<div class="state">Loading the ${noun(step?.contributors, 2)}…</div>`;
    else {
      const gap = Math.abs(c.total_hours - c.step_hours);
      body = html`
        <div class="sub">
          ${count(c.count)} ${noun(c.step.contributors, c.count)} ·
          ${formatHours(c.visible_hours, { exact: true })} listed
          ${
            c.withheld_count > 0
              ? html` · ${count(c.withheld_count)} outside your scope hold
                ${formatHours(c.withheld_hours, { exact: true })}`
              : nothing
          }
          · step ${formatHours(c.step_hours, { exact: true })}
        </div>
        ${
          gap > 0.011
            ? html`<p class="note warn">
                The contributors total ${formatHours(c.total_hours, { exact: true })}, not the
                step's ${formatHours(c.step_hours, { exact: true })} — report this.
              </p>`
            : nothing
        }
        <data-table
          caption=${`${c.step.label} — ${this._scopeLabel()}`}
          export-name=${`capacity-bridge-${c.step.key}`}
          page-size="15"
          dense
          .columns=${this._contribColumns}
          .rows=${c.contributors}
        ></data-table>
      `;
    }
    return html`
      <section class="contributors" aria-live="polite" aria-busy=${this._contribLoading}>
        <div class="bar">
          <h3>Behind ${step?.label ?? this._step}</h3>
          <span class="sub">${this._scopeLabel()}</span>
          <button class="close" @click=${() => this._selectStep(this._step)}>Close</button>
        </div>
        ${body}
      </section>
    `;
  }

  render() {
    const { run } = this.appState;
    const d = this._data;
    const wf = this._waterfall;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!d && this._loading) body = html`<div class="state">Loading the bridge…</div>`;
    else if (!d || d.weeks.length === 0)
      body = html`<div class="state">No weeks in this window.</div>`;
    else
      body = html`
        ${this._weekSelect()}
        <chart-frame
          .categories=${this._steps.map((s) => s.key)}
          .min=${wf.min}
          .max=${wf.max}
          .marks=${this._marks}
          .formatCategory=${(c) => AXIS_LABELS[c] ?? c}
          band-padding="0.32"
          value-label="Hours"
          category-label="From gross capacity to headroom"
          height="300"
        ></chart-frame>
        ${this._legend()}
        ${
          wf.adds_up
            ? nothing
            : html`<p class="note warn">
                The steps add up to ${formatHours(wf.sum_check, { exact: true })}, not the headroom
                of ${formatHours(wf.headroom_hours, { exact: true })} — report this.
              </p>`
        }
        ${this._contributors()}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`Capacity bridge — ${this._scopeLabel()}`}
            export-name="capacity-bridge"
            page-size="10"
            dense
            .columns=${this._stepColumns}
            .rows=${this._stepRows}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Capacity bridge</div>
            <h2>How do we get from gross hours to headroom?</h2>
            <div class="sub">${this._subtitle()}</div>
          </div>
          ${this._kpis()}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('capacity-bridge', CapacityBridge);
